import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus } from 'lucide-react'
import { CONTENT_TYPES, type ContentType } from '@mlopsmena/content'
import { api, type Item } from '../lib/api'
import { TYPES, type TypeConfig } from '../lib/types'
import { missingArabic } from '../lib/formValues'
import { Empty, Notice, PageHeader, Spinner, StatusChip, ago } from '../components/ui'

const FILTERS = ['all', 'published', 'draft', 'archived'] as const

export default function ContentList() {
  const type = useParams().type as ContentType
  if (!CONTENT_TYPES.includes(type)) return <Notice>Unknown content type.</Notice>
  return <List key={type} config={TYPES[type]} />
}

function List({ config }: { config: TypeConfig }) {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['content', config.type],
    queryFn: () => api.get<{ items: Item[] }>(`/content/${config.type}`),
  })
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all')
  const [order, setOrder] = useState<Item[]>([])
  useEffect(() => { if (query.data) setOrder(query.data.items) }, [query.data])

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.post<{ items: Item[] }>(`/content/${config.type}/reorder`, { ids }),
    onSuccess: (data) => qc.setQueryData(['content', config.type], data),
    onError: () => query.refetch(),
  })

  const shown = order.filter((i) => filter === 'all' || i.status === filter)
  const canDrag = config.reorder && filter === 'all'

  const sections = [{ title: null as string | null, items: shown }]

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  function onDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return
    const from = order.findIndex((i) => i.id === e.active.id)
    const to = order.findIndex((i) => i.id === e.over!.id)
    const next = arrayMove(order, from, to)
    setOrder(next)
    reorder.mutate(next.map((i) => i.id))
  }

  return (
    <>
      <PageHeader title={config.label}>
        <div className="flex rounded-lg border border-line p-0.5">
          {FILTERS.map((f) => (
            <button key={f} className={`rounded-md px-2.5 py-1 text-xs ${filter === f ? 'bg-surface-2 font-semibold' : 'text-muted'}`}
                    onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
        <Link to={`/content/${config.type}/new`} className="btn-primary"><Plus className="h-3.5 w-3.5" /> New {config.singular}</Link>
      </PageHeader>

      {query.isPending && <Spinner />}
      {query.isError && <Notice>{(query.error as Error).message}</Notice>}
      {reorder.isError && <Notice>Could not save the new order: {(reorder.error as Error).message}</Notice>}
      {query.data && shown.length === 0 && <Empty>No {filter === 'all' ? '' : `${filter} `}{config.label.toLowerCase()} yet.</Empty>}
      {canDrag && shown.length > 1 && <p className="mb-2 text-xs text-faint">Drag rows to change the order on the site.</p>}

      {sections.map((s) => (
        <div key={s.title ?? 'all'} className="mb-6">
          {s.title && <h2 className="mb-2 text-sm font-semibold text-muted">{s.title}</h2>}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={s.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ul className="card divide-y divide-line">
                {s.items.map((item) => <Row key={item.id} item={item} config={config} draggable={canDrag} />)}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      ))}
    </>
  )
}

function Row({ item, config, draggable }: { item: Item; config: TypeConfig; draggable: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !draggable })
  const missing = missingArabic(item, config.englishOnly)
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-3 px-3 py-2.5 ${isDragging ? 'relative z-10 bg-surface-2 shadow-lg' : ''}`}
    >
      {draggable && (
        <button className="cursor-grab text-faint hover:text-fg" aria-label="Drag to reorder" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <Link to={`/content/${config.type}/${item.id}`} className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium hover:text-accent">{config.title(item) || item.slug}</p>
        <p className="truncate text-xs text-faint">{config.subtitle?.(item) ?? item.slug}</p>
      </Link>
      {missing.length > 0 && (
        <span className="chip border-warn/40 text-warn" title={`No Arabic for: ${missing.join(', ')}`}>AR {missing.length}</span>
      )}
      <StatusChip status={item.status} />
      <span className="hidden w-24 text-end text-xs text-faint sm:block">{ago(item.updatedAt)}</span>
    </li>
  )
}
